import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Star } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * Reviews + rating UI for a single work.
 * Renders average rating, individual reviews, and a form for the current user.
 */
export default function WorkReviews({ workId, testIdPrefix = 'work' }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], stats: { average_rating: 0, review_count: 0, distribution: {} } });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workId]);

  const load = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/works/${workId}/reviews`);
      setData(res.data);
      const mine = res.data.reviews.find(r => user && r.user_id === user.user_id);
      if (mine) {
        setRating(mine.rating);
        setText(mine.review_text || '');
      }
    } catch (e) { /* silent */ }
  };

  const submit = async () => {
    if (!user) return toast.error('Please sign in to review');
    if (!rating) return toast.error('Please pick a rating');
    try {
      setSubmitting(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/works/${workId}/reviews`,
        { rating, review_text: text },
        { withCredentials: true }
      );
      toast.success('Review saved');
      load();
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const avg = data.stats.average_rating || 0;
  const count = data.stats.review_count || 0;

  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid={`${testIdPrefix}-reviews-${workId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                size={16}
                weight={n <= Math.round(avg) ? 'fill' : 'regular'}
                className={n <= Math.round(avg) ? 'text-amber-500' : 'text-muted-foreground'}
              />
            ))}
          </div>
          <span className="text-sm font-semibold" data-testid={`avg-rating-${workId}`}>
            {avg.toFixed(1)}
          </span>
          <span className="text-xs text-muted-foreground" data-testid={`review-count-${workId}`}>
            ({count} review{count !== 1 ? 's' : ''})
          </span>
        </div>
        {count > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            data-testid={`toggle-reviews-${workId}`}
            className="text-xs text-primary hover:underline"
          >
            {expanded ? 'Hide' : 'Show'} reviews
          </button>
        )}
      </div>

      {expanded && data.reviews.length > 0 && (
        <div className="space-y-2 mb-3">
          {data.reviews.map((r) => (
            <div key={r.review_id} className="bg-muted/30 rounded p-3" data-testid={`review-${r.review_id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold">{r.user_name}</span>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={12} weight={n <= r.rating ? 'fill' : 'regular'} className={n <= r.rating ? 'text-amber-500' : 'text-muted-foreground'} />
                  ))}
                </div>
              </div>
              {r.review_text && <p className="text-sm text-foreground/80">{r.review_text}</p>}
            </div>
          ))}
        </div>
      )}

      {user && (
        <div className="bg-primary/5 rounded p-3" data-testid={`review-form-${workId}`}>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Your Rating</p>
          <div className="flex items-center gap-1 mb-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                data-testid={`star-${workId}-${n}`}
                className="hover:scale-110 transition-transform"
              >
                <Star
                  size={22}
                  weight={n <= (hover || rating) ? 'fill' : 'regular'}
                  className={n <= (hover || rating) ? 'text-amber-500' : 'text-muted-foreground'}
                />
              </button>
            ))}
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Share your thoughts (optional)"
            rows={2}
            data-testid={`review-text-${workId}`}
            className="w-full text-sm px-3 py-2 bg-background border border-border rounded outline-none focus:border-primary mb-2"
          />
          <button
            onClick={submit}
            disabled={!rating || submitting}
            data-testid={`submit-review-${workId}`}
            className="bg-primary text-primary-foreground text-xs rounded-full py-1.5 px-4 font-semibold disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Submit Review'}
          </button>
        </div>
      )}
    </div>
  );
}
